import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, MapPin, Sparkles } from 'lucide-react';
import { useAddToChat } from '../../../../../components/chat/chatContext';
import { getBrazilianStatus } from '../../../../../utils/detectBrazil';
import { nbaDashPtBR } from '../../../../../components/mappings/sports-dash-mappings/nba-dash.pt-br';
import MobileNBAScoreGrid from "./MobileNBAScoreGrid";
import PredictButton from "../../../../../components/dashboards/components/shared/PredictButton";
import XPicksButton from "../../../../../components/dashboards/components/shared/XPicksButton";
import ViewOddsButton from "../../../../../components/dashboards/components/shared/ViewOddsButton";
import PredictionBox from "../../../../../components/dashboards/components/shared/PredictionBox";

const NBAMobileGameCard = memo(({
  game,
  onViewOdds,
  isSelected = false,
  isFrozen = false,
  onToggleSelection = null,
  onViewPrediction,
  prediction = null,
  showPredictionButton = false
}) => {
  // Internationalization
  const isBrazilian = getBrazilianStatus();
  const t = (text) => isBrazilian ? (nbaDashPtBR[text] || text) : text;

  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction) return;

    const message = `${t('Analyze this NBA prediction:')}

${teams?.visitors?.name} @ ${teams?.home?.name}

${t('Prediction:')} ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% ${t('confidence')})` : ''}
${t('Confidence Level:')} ${prediction.confidence}
${prediction.reason ? `${t('Reasoning:')} ${prediction.reason}` : ''}

${t('Can you provide a deeper analysis of this matchup?')}`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  if (!game) return null;

  const { teams, scores, status, date, arena } = game;

  // Status short values: 1=Scheduled, 2=Live, 3=Finished, 4=Postponed, 5=Delayed, 6=Canceled
  const statusShort = status?.short;
  const isLive = statusShort === 2;
  const isFinished = statusShort === 3;
  const isUpcoming = statusShort === 1;
  const predictLabel = isFrozen
    ? t('COOLDOWN')
    : isSelected
    ? t('SELECTED')
    : isLive
    ? t('PREDICT LIVE')
    : t('PREDICT');

  // Get display text for status badge
  const getStatusDisplay = () => {
    if (isLive) {
      const currentPeriod = game.periods?.current;
      if (currentPeriod) {
        const periodDisplay = currentPeriod > 4 ? t('OT') : `${t('Q')}${currentPeriod}`;
        if (status?.clock) {
          return `${periodDisplay} ${status.clock}`;
        }
        return `${t('LIVE')} ${periodDisplay}`;
      }
      return t('LIVE');
    }
    if (isFinished) return t('FINAL');
    return null;
  };

  const statusDisplay = getStatusDisplay();

  // Format game time
  const formatGameTime = () => {
    if (isLive) return t('LIVE');
    if (isFinished) return t('Final');

    if (date?.start) {
      try {
        const gameDate = new Date(date.start);

        if (!isNaN(gameDate.getTime())) {
          const options = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/New_York'
          };
          return `${gameDate.toLocaleTimeString('en-US', options)} ${t('ET')}`;
        }
      } catch (e) {
        // Fall through
      }
    }

    return t('TBD');
  };

  return (
    <div
      className={`relative border border-white/10 px-4 py-3 bg-white/[0.03] transition-all`}
    >
      {/* Status Badge - Top Right */}
      {statusDisplay && (
        <div className="absolute top-2 right-2 z-10">
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
            isLive ? 'bg-red-500' : 'bg-white/20'
          }`}>
            {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
            <span className="text-[10px] font-mono text-white">
              {statusDisplay}
            </span>
          </div>
        </div>
      )}

      {/* League Label */}
      <div className="text-[10px] text-white/60 font-semibold uppercase tracking-[0.28em] mb-3 truncate pr-16">
        {game.league || t('NBA')}
      </div>

      {/* Teams and Scores */}
      <div className="space-y-2.5 mt-4">
        {/* Visitors Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {teams?.visitors?.logo && (
              <img
                src={teams.visitors.logo}
                alt={teams.visitors.name}
                className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40 flex-shrink-0"
                loading="lazy"
              />
            )}
            <span className="text-sm font-mono text-white tracking-tight leading-tight truncate">
              {teams?.visitors?.name || 'Visitors'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-2 tabular-nums">
            {isFinished || isLive ? (scores?.visitors?.points ?? '-') : '-'}
          </span>
        </div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {teams?.home?.logo && (
              <img
                src={teams.home.logo}
                alt={teams.home.name}
                className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40 flex-shrink-0"
                loading="lazy"
              />
            )}
            <span className="text-sm font-mono text-white tracking-tight leading-tight truncate">
              {teams?.home?.name || 'Home'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-2 tabular-nums">
            {isFinished || isLive ? (scores?.home?.points ?? '-') : '-'}
          </span>
        </div>
      </div>

      {/* Game Time and Venue - only for upcoming games */}
      {!isFinished && !isLive && (
        <div className="mt-3 pt-2 border-t border-white/10">
          <div className="flex items-center justify-center gap-1 text-white/60 text-[10px] font-semibold tracking-[0.2em] uppercase">
            <Clock size={10} />
            <span>
              {formatGameTime()}
            </span>
            {arena?.name && (
              <>
                <span className="text-white/30">•</span>
                <MapPin size={9} />
                <span className="text-[10px] font-medium truncate">
                  {arena.name}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Prediction Result (if available) */}
      {prediction && (
        <PredictionBox
          prediction={prediction}
          onAnalyze={handleAnalyzeInChat}
          variant="mobile"
          className="mt-3 rounded-sm"
          t={t}
        />
      )}

      {/* Action Buttons */}
      <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: teams?.home?.name,
                awayTeam: teams?.visitors?.name,
                homeTeamId: teams?.home?.id,
                visitorsTeamId: teams?.visitors?.id,
                leagueId: game.league?.id || 12,
                date: date?.start,
                time: formatGameTime(),
                gameId: game?.id
              }}
              sport="nba"
              label={t('XPICKS')}
            />
          </div>
        )}

        {showPredictionButton && (isUpcoming || isLive) && onToggleSelection && (
          <div className="col-span-1">
            <PredictButton
              label={predictLabel}
              isSelected={isSelected}
              isLive={isLive}
              isFrozen={isFrozen}
              onClick={(e) => {
                e.stopPropagation();
                if (!isFrozen) {
                  onToggleSelection(game);
                  if (onViewPrediction && !isSelected) {
                    onViewPrediction(game);
                  }
                }
              }}
            />
          </div>
        )}

        {/* Disabled until integration with the API to show odds
        {onViewOdds && (
          <div className="col-span-1">
            <ViewOddsButton
              label={t('VIEW ODDS')}
              icon={Eye}
              onClick={() => onViewOdds(game)}
            />
          </div>
        )}
        */}
      </div>

      {/* Quarter Scores for Live/Finished Games */}
      {(isLive || isFinished) && scores?.visitors?.linescore && scores?.home?.linescore && (
        <MobileNBAScoreGrid teams={teams} scores={scores} t={t} />
      )}
    </div>
  );
});

NBAMobileGameCard.displayName = 'MobileNBAGameCard';

export default NBAMobileGameCard;
